import { useEffect, useState } from "react";
import { adminOperationError } from "@/app/adminOperationError";

const POLL_MS = 5_000;

/**
 * 조회 하나를 마운트된 동안 5초마다 다시 보낸다.
 *
 * - 직전 응답이 온 뒤에 다음 조회를 예약한다. 그래서 조회가 겹치지 않는다.
 * - 화면이 가려져 있으면 조회를 건너뛰고 다음 예약만 한다.
 * - 조회가 실패해도 마지막 값을 지우지 않는다. 오류 문구만 바꾸고 폴링을 계속한다.
 *
 * `request` 가 바뀌면 값을 비우고 처음부터 조회한다. 호출부는 `useCallback` 으로 참조를 고정한다.
 */
export function usePolledRequest<T>(
  request: () => Promise<T>,
  reloadKey = 0,
): {
  data: T | null;
  error: string | null;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setData(null);
    setError(null);
    const schedule = () => {
      if (!cancelled) timer = setTimeout(() => void load(), POLL_MS);
    };
    const load = async () => {
      if (document.visibilityState === "hidden") {
        schedule();
        return;
      }
      try {
        const next = await request();
        if (!cancelled) {
          setData(next);
          setError(null);
        }
      } catch (caught) {
        if (!cancelled) setError(adminOperationError(caught));
      } finally {
        schedule();
      }
    };
    void load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [request, reloadKey]);
  return { data, error };
}
